/**
 * Analytics Types
 * Type-safe analytics tracking for colorscales.io
 */

// Analytics provider interface for abstraction
export interface AnalyticsProvider {
  capture(event: string, properties?: Record<string, unknown>): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
  alias(newId: string, oldId?: string): void;
  reset(): void;
}

// Event names as const for type safety
export const AnalyticsEvents = {
  // Core conversion funnel
  PAGE_VIEWED: "page_viewed",
  EMAIL_CAPTURED: "email_captured",
  EMAIL_CTA_CLICKED: "email_cta_clicked",
  EMAIL_VALIDATION_FAILED: "email_validation_failed",
  CTA_CLICKED: "cta_clicked",
  DEMO_INTERACTED: "demo_interacted",

  // Engagement metrics
  SECTION_VIEWED: "section_viewed",
  VIDEO_PLAYED: "video_played",
  PRICING_VIEWED: "pricing_viewed",
  THEME_TOGGLED: "theme_toggled",

  // Conversion tracking
  INSTALL_INTENT_CREATED: "install_intent_created",
  PLUGIN_INSTALL_CLICKED: "plugin_install_clicked",
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// Event property types for type safety
export interface PageViewedProperties {
  page: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
}

export interface EmailCapturedProperties {
  source: "hero" | "footer" | "cta" | "demo";
  email_domain: string;
}

export interface EmailCTAClickedProperties {
  location: "hero" | "footer" | "cta" | "demo";
  has_email_value: boolean;
}

export interface EmailValidationFailedProperties {
  location: "hero" | "footer" | "cta" | "demo";
  error_type: "empty" | "invalid_format";
}

export interface CTAClickedProperties {
  cta_text: string;
  cta_location: "hero" | "features" | "pricing" | "footer" | "final-cta";
}

export interface DemoInteractedProperties {
  interaction_type: "color_change" | "theme_change" | "export" | "import" | "randomize_colors";
  demo_location: "hero" | "features";
}

export interface SectionViewedProperties {
  section: string;
  scroll_depth?: number;
}

export interface VideoPlayedProperties {
  video_id: string;
  video_location: string;
}

export interface PricingViewedProperties {
  plan_viewed?: string;
}

export interface ThemeToggledProperties {
  from_theme: "light" | "dark";
  to_theme: "light" | "dark";
}

export interface InstallIntentCreatedProperties {
  install_intent_id: string;
  fingerprint: string;
  user_signals: {
    timezone: string;
    language: string;
    screen_resolution: string;
  };
  timestamp: number;
  cta_location: PluginInstallClickedProperties["cta_location"];
  page_path: string;
  page_location?: string;
  store_success?: boolean;
  anonymous_id?: string;
  ref?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
}

export interface PluginInstallClickedProperties {
  destination_url: string;
  cta_location: "header" | "hero" | "features" | "pricing" | "footer" | "final-cta";
}

// Union type of all event properties
export type EventProperties =
  | PageViewedProperties
  | EmailCapturedProperties
  | EmailCTAClickedProperties
  | EmailValidationFailedProperties
  | CTAClickedProperties
  | DemoInteractedProperties
  | SectionViewedProperties
  | VideoPlayedProperties
  | PricingViewedProperties
  | ThemeToggledProperties
  | InstallIntentCreatedProperties
  | PluginInstallClickedProperties
  | Record<string, unknown>;

